import type { Answer } from '@/types/respondent';

/**
 * Prepends a new Answer object as the first/most recent answer to the item in the answer map. Only add the new answer
 * if it has different options/values from the previous answer for the same item.
 *
 * @param answer - The timestamped answer to record.
 * @param answerMap - A map of item IDs to arrays of Answer objects.
 * @returns A new answer map with the answer conditionally appended.
 */
export function prependAnswer(
  answer: Answer,
  answerMap: Map<string, Answer[]>
): Map<string, Answer[]> {
  const itemId = answer.item;
  const existingAnswers = answerMap.get(itemId) ?? [];
  const previousAnswer = existingAnswers[0] ?? null; // Assumes list is sorted most recent to oldest
  const isAnswerIdentical = checkIfAnswerIsIdentical(answer, previousAnswer);

  if (isAnswerIdentical) {
    return answerMap; // no change, return same reference
  }

  const next = new Map(answerMap);
  next.set(itemId, [answer, ...existingAnswers]);
  return next;
}

/**
 * Determine if the `maxSecondsBetweenSaves` threshold has been met.
 *
 * @param maxSecondsBetweenSaves - The maximum number of seconds to elapse between save attempts.
 * @param currentTime - The current time.
 * @param dateOfFirstUnsavedAnswer - The date of the earliest answer that has not yet been saved, if there is one.
 * @returns A boolean indicating whether to trigger a save due to the elapsed time.
 */
export function checkMaxSecondsBetweenSaves(
  maxSecondsBetweenSaves: number,
  currentTime: Date,
  dateOfFirstUnsavedAnswer: Date | null
): boolean {
  // When maxSecondsBetweenSaves set to 0, always return false. Time-based saves are disabled.
  // Likewise, when there is no first unsaved reply date, there's nothing to save.
  if (!maxSecondsBetweenSaves || !dateOfFirstUnsavedAnswer) return false;

  const secondsSinceFirstUnsavedAnswer: number = (currentTime.getTime() - dateOfFirstUnsavedAnswer.getTime()) / 1000;
  return secondsSinceFirstUnsavedAnswer >= maxSecondsBetweenSaves;
}

/**
 * Compare two answers to check if they are identical. Identical means:
 * (1) they are for the same item,
 * (2) they have the same value,
 * (3) they have the same selected options in the same order.
 * Differences in answer IDs and timestamps are ignored.
 *
 * @param newAnswer - The current answer.
 * @param previousAnswer - The previous answer to the same item.
 * @returns `true` if the answers are identical; otherwise `false`.
 */
function checkIfAnswerIsIdentical(
  newAnswer: Answer,
  previousAnswer: Answer | null | undefined
): boolean {
  if (!previousAnswer || previousAnswer.deletedAt) return false;

  const optionsNew = newAnswer.options ?? [];
  const optionsPrev = previousAnswer.options ?? [];

  return (
    previousAnswer.value === newAnswer.value &&                                        // Same value
    optionsNew.length === optionsPrev.length &&                                        // Same number of options
    optionsNew.every((val: string, idx: number): boolean => val === optionsPrev[idx])  // Options are in the same order
  );
}

/**
 * Find the date of the earliest unsaved answer.
 *
 * @param unsaved - A map of unsaved answers. Keys are answer ids, values are item ids.
 * @param answerMap - A map of all answers, keyed by answer id.
 * @returns The date of the earliest unsaved answer, or null if none found.
 */
export function getDateOfFirstUnsavedAnswer(
  unsaved: Map<string, string>,
  answerMap: Map<string, Answer[]>
): Date | null {
  if (unsaved.size === 0) return null;

  const createdAtDates: Date[] = [];

  for (const [answerId, itemId] of unsaved) {
    const answer: Answer | undefined = answerMap.get(itemId)?.find(a => a.id = answerId);
    if (answer) createdAtDates.push(new Date(answer.createdAt));
  }

  if (createdAtDates.length === 0) return null;

  return createdAtDates.reduce((earliest, current) =>
    current < earliest ? current : earliest
  );
}

/**
 * Convert a flat list of Answer objects into an AnswerMap grouped by item ID.
 *
 * Each key in the resulting map is an item's UUID (answer.item), and each value is
 * an array of answers for that item, sorted by descending createdAt timestamp (the most recent answer first).
 *
 * @param answers - A list of Answer objects, potentially unordered.
 * @returns A map where keys are item IDs and values are lists of answers sorted from most recent to oldest.
 */
export function toAnswerMap(answers: Answer[]): Map<string, Answer[]> {
  const answerMap = new Map<string, Answer[]>();

  for (const answer of answers) {
    const itemId = answer.item;
    if (answerMap.has(itemId)) {
      answerMap.get(itemId)!.push(answer);
    } else {
      answerMap.set(itemId, [answer]);
    }
  }

  for (const itemAnswers of answerMap.values()) {
    itemAnswers.sort(
      (a: Answer, b: Answer) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  return answerMap;
}

/**
 * Convert an answer map to a flat list of unsaved answers.
 *
 * @param answerMap - A map where keys are item IDs and values are arrays of Answer objects.
 * @param unsaved - A set of stubs, each identifying exactly one unsaved answer by its `answerId` and `itemId`.
 * @returns A flat array of `Answer` objects corresponding to the unsaved stubs.
 */
export function toUnsavedAnswers(
  answerMap: Map<string, Answer[]>,
  unsaved: Map<string, string>
): Answer[] {
  if (unsaved.size === 0) return [];

  const unsavedAnswers: Answer[] = [];

  for (const [answerId, itemId] of unsaved) {
    const answer: Answer | undefined = answerMap.get(itemId)?.find(a => a.id === answerId);
    if (answer) unsavedAnswers.push(answer);
  }
  return unsavedAnswers;
}